import { computed } from 'vue';

import { amplitudeLib } from '../../shared/lib';
import { CROSS_APP_CACHED_EXPERIMENTS } from './config';

const EXPERIMENT_CACHE_PREFIX = 'free:exp:';

const getCachedVariant = (experimentKey: string): string | null => {
  try {
    return localStorage.getItem(`${EXPERIMENT_CACHE_PREFIX}${experimentKey}`);
  } catch {
    return null;
  }
};

const setCachedVariant = (experimentKey: string, variant: string) => {
  try {
    localStorage.setItem(`${EXPERIMENT_CACHE_PREFIX}${experimentKey}`, variant);
  } catch {
    // localStorage may be unavailable
  }
};

export const checkExperimentVariant = (
  experimentKey: string,
  variantToCheck: string,
) => {
  const { experiment, isExperimentLoading } =
    amplitudeLib.useAmplitudeExperiment();

  const isCrossAppCached = CROSS_APP_CACHED_EXPERIMENTS.has(experimentKey);
  const cachedVariant = isCrossAppCached
    ? getCachedVariant(experimentKey)
    : null;

  const currentVariant = computed(() => {
    const serverVariant = experiment.value?.variant?.(experimentKey)?.key;

    if (isCrossAppCached) {
      // While experiment is still loading, the SDK may return a stale variant
      // from its own localStorage cache (e.g. written by railways with a
      // different user ID). Use our own cache to prevent UI flicker.
      if (isExperimentLoading.value) {
        return cachedVariant ?? serverVariant ?? null;
      }

      // After loading completes we have a fresh server response — update cache.
      if (serverVariant) {
        setCachedVariant(experimentKey, serverVariant);
      }

      return serverVariant ?? cachedVariant;
    }

    return serverVariant ?? null;
  });

  return {
    isExperimentLoading,
    isVariant: computed(() => currentVariant.value === variantToCheck),
  };
};

/**
 * Reads the current variant key for an experiment from the SDK without going
 * through a Vue computed/ref. Use from non-reactive contexts such as analytics
 * event helpers that fire on a later page (after MainPage is unmounted) and
 * still need to attach the originally-resolved variant to event properties.
 *
 * Returns `null` when the SDK has not yet returned a variant (e.g. before the
 * user has visited a page that calls `checkExperimentVariant`).
 */
export const getCurrentVariant = (experimentKey: string): string | null => {
  const { experiment } = amplitudeLib.useAmplitudeExperiment();
  return experiment.value?.variant?.(experimentKey)?.key ?? null;
};

/**
 * Returns a cached-only experiment variant.
 * Does NOT use server response and does NOT write to cache.
 * Use in apps where Amplitude user ID differs from other apps (e.g. railways),
 * so the server variant would be incorrect for shared experiments.
 *
 * @param defaultVariant - fallback when cache is empty
 */
export const checkCachedExperimentVariant = (
  experimentKey: string,
  variantToCheck: string,
  defaultVariant: string,
) => {
  const { isExperimentLoading } = amplitudeLib.useAmplitudeExperiment();

  const cachedVariant = getCachedVariant(experimentKey);
  const currentVariant = cachedVariant ?? defaultVariant;

  return {
    isExperimentLoading,
    isVariant: computed(() => currentVariant === variantToCheck),
  };
};
